import type { SidebarEntry } from "@/api/sidebarEntryAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faWavePulse } from "@fortawesome/sharp-regular-svg-icons";
import { Link } from "@tanstack/react-router";
import { Badge, Collapsible, CollapsibleContent, CollapsibleTrigger } from "frontend-components";
import { useTranslation } from "react-i18next";

type PulseEntry = Extract<SidebarEntry, { type: "PULSE" }>;
export type PulseRouteEntry = Extract<SidebarEntry, { type: "PULSE"; isUpsell: false }> & {
    navigation: Extract<NonNullable<SidebarEntry["navigation"]>, { type: "PULSE" }>;
};

type PulseRouteTarget = { to: "/$customerId/pulse/$pulseId" };

const linkClassName =
    "text-foreground flex px-5 py-2.5 transition-colors hover:bg-muted data-[status=active]:bg-muted";

const nestedLinkClassName =
    "text-foreground flex px-5 py-2 pl-11 transition-colors hover:bg-muted data-[status=active]:bg-muted";

function getPulseRouteTarget(mountPath: string): PulseRouteTarget | null {
    if (mountPath === "/pulse") {
        return { to: "/$customerId/pulse/$pulseId" };
    }

    return null;
}

function PulseRouteContent({
    entry,
    compact = false,
}: {
    entry: PulseRouteEntry;
    compact?: boolean;
}) {
    return (
        <span className="min-w-0 flex-1">
            <span
                className={`block truncate leading-[1.25] font-medium ${
                    compact ? "text-[13px]" : "text-[14px]"
                }`}
            >
                {entry.productInstanceName ?? entry.name}
            </span>
        </span>
    );
}

function PulseRouteLink({
    entry,
    customerId,
    nested = false,
}: {
    entry: PulseRouteEntry;
    customerId: number;
    nested?: boolean;
}) {
    const routeTarget = getPulseRouteTarget(entry.navigation.mountPath);
    const className = nested ? nestedLinkClassName : linkClassName;
    const content = <PulseRouteContent entry={entry} compact={nested} />;

    if (!routeTarget) {
        return (
            <a href={entry.url ?? "#"} className={className}>
                {content}
            </a>
        );
    }

    return (
        <Link
            to={routeTarget.to}
            params={{
                customerId: String(customerId),
                pulseId: String(entry.navigation.pulseInstanceId),
            }}
            className={className}
            activeOptions={{ exact: false }}
        >
            {content}
        </Link>
    );
}

export default function SidebarPulseEntry({
    entry,
    customerId,
    pulseRouteEntries,
    singlePulseRouteEntry,
}: {
    entry: PulseEntry;
    customerId: number;
    pulseRouteEntries: PulseRouteEntry[];
    singlePulseRouteEntry: PulseRouteEntry | null;
}) {
    const { t } = useTranslation();

    if (entry.isUpsell) {
        return (
            <a href={entry.url ?? "#"} className={linkClassName}>
                <span className="flex w-full min-w-0 items-center gap-2.5">
                    <span className="text-muted-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center">
                        <FontAwesomeIcon icon={faWavePulse} />
                    </span>
                    <span className="block min-w-0 flex-1 truncate text-[14px] leading-[1.25] font-medium">
                        {entry.name}
                    </span>
                    <Badge
                        className="text-primary-foreground ml-auto border-transparent px-2.5 text-[10px] font-semibold tracking-[0.02em]"
                        style={{
                            background: "var(--compendia-gradient)",
                        }}
                        variant="dark"
                    >
                        {t("hub.try")}
                    </Badge>
                </span>
            </a>
        );
    }

    if (singlePulseRouteEntry) {
        return <PulseRouteLinkWithIcon entry={singlePulseRouteEntry} customerId={customerId} />;
    }

    return (
        <Collapsible defaultOpen>
            <CollapsibleTrigger className="text-foreground flex w-full cursor-pointer items-center gap-2.5 px-5 py-2.5 text-left transition-colors hover:bg-muted data-[panel-open]:[&_.sidebar-chevron]:rotate-180">
                <span className="text-muted-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <FontAwesomeIcon icon={faWavePulse} />
                </span>
                <span className="block min-w-0 flex-1 truncate text-[14px] leading-[1.25] font-medium">
                    {entry.name}
                </span>
                <span className="text-muted-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center">
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className="sidebar-chevron transition-transform"
                    />
                </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="flex flex-col gap-0.5 pb-1">
                    {pulseRouteEntries.map((pulseEntry) => (
                        <PulseRouteLink
                            key={pulseEntry.key}
                            entry={pulseEntry}
                            customerId={customerId}
                            nested
                        />
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

function PulseRouteLinkWithIcon({
    entry,
    customerId,
}: {
    entry: PulseRouteEntry;
    customerId: number;
}) {
    const routeTarget = getPulseRouteTarget(entry.navigation.mountPath);
    const content = (
        <span className="flex w-full min-w-0 items-center gap-2.5">
            <span className="text-muted-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center">
                <FontAwesomeIcon icon={faWavePulse} />
            </span>
            <PulseRouteContent entry={entry} />
        </span>
    );

    if (!routeTarget) {
        return (
            <a href={entry.url ?? "#"} className={linkClassName}>
                {content}
            </a>
        );
    }

    return (
        <Link
            to={routeTarget.to}
            params={{
                customerId: String(customerId),
                pulseId: String(entry.navigation.pulseInstanceId),
            }}
            className={linkClassName}
            activeOptions={{ exact: false }}
        >
            {content}
        </Link>
    );
}
